export type AdminOutletContext = {
  globalSearch: string;
  setGlobalSearch: (value: string) => void;
};

export type ClientSource = 'landing' | 'manual';

export interface Client {
  id: string;
  name: string;
  /** Empty when the lead did not leave an email on the landing page. */
  email: string;
  phone?: string;
  /** Internal CRM notes (not sent to client). */
  notes?: string;
  requestDate: string;
  status: 'pending' | 'quoted' | 'paid';
  origin?: string;
  destination?: string;
  travelDate?: string;
  source?: ClientSource;
}

export type FlightSegment = {
  airline: string;
  flightNumber: string;
  departure: string;
  arrival: string;
  /** Full airport name from flight search API (e.g. Beijing Capital International Airport). */
  departureName?: string;
  arrivalName?: string;
  departureTime: string;
  arrivalTime: string;
  duration?: number;
  airplane?: string;
  travelClass?: string;
  /** e.g. "Hot meal", "DINNER" — from SerpAPI extensions or admin */
  mealOptions?: string;
};

export type FlightLayover = { duration: number; name: string; id: string; overnight?: boolean };

/** One direction of travel (outbound is on Flight; return is optional returnLeg). */
export interface FlightLeg {
  airline: string;
  flightNumber: string;
  departure: string;
  arrival: string;
  /** Full airport name from flight search API (first/last segment). */
  departureName?: string;
  arrivalName?: string;
  departureTime: string;
  arrivalTime: string;
  travelDate?: string;
  totalDuration?: number;
  stopsCount?: number;
  layovers?: FlightLayover[];
  segments?: FlightSegment[];
  /** SerpAPI reference fare for this leg (round-trip return options). */
  referencePrice?: number;
}

export interface Flight extends FlightLeg {
  id: string;
  price: number;
  /** 0–23 local, for approximate-time filters */
  departureHour: number;
  rawExtensions?: string[];
  /** SerpAPI token to load return options (round trip outbound results). */
  departureToken?: string;
  /** Inbound leg for round trips (filled before quote send). */
  returnLeg?: FlightLeg;
  /** Outbound-only list fare when returnLeg is set (round trip). */
  outboundReferencePrice?: number;
  tripType?: '1' | '2';
  returnDate?: string;
}

export interface Agent {
  id: string;
  name: string;
  nickname: string;
  email: string;
  avatar?: string;
}

/** Traveler on a quote — name and age (contact email lives on clientSnapshot). */
export type QuotePassenger = {
  name: string;
  /** Adult age in years (required when creating a quote). */
  age?: number;
  /** Date of birth (YYYY-MM-DD), derived from age or entered explicitly. */
  dateOfBirth?: string;
  /** Saved from booking / payment forms. */
  gender?: 'male' | 'female';
  /** First / middle / last for form re-hydration (optional; name is canonical). */
  firstName?: string;
  middleName?: string;
  lastName?: string;
  /** Per-passenger e-ticket number (assigned when the quote is created). */
  eTicketNumber?: string;
};

export type QuoteRecord = {
  id: string;
  clientId: string;
  agentId: string;
  flight: Flight;
  pricePerPerson: number;
  clientSnapshot: { name: string; email: string; phone?: string };
  /** All travelers; when omitted, clientSnapshot.name is treated as passenger 1. */
  passengers?: QuotePassenger[];
  /** Checked bags count from quote search (0–9). */
  bags?: number;
  /** Adults count from search when the quote was created (client completes names on booking). */
  adults?: number;
  createdAt: string;
  /** Set when the client saves passenger details on the booking page. */
  bookingUpdatedAt?: string;
  bookingUrl: string;
  paymentUrl: string;
  paymentStatus: 'unpaid' | 'paid';
  /** PNR-style code on the receipt (e.g. R28SSK). */
  confirmationNumber?: string;
  /** Numeric-style passenger ticket number shown on the e-ticket. */
  eTicketNumber?: string;
  /** ISO timestamps for client emails tied to this quote. */
  emailsSent?: {
    quote?: string;
    /** OAF / payment checkout link emailed with passenger details. */
    oaf?: string;
    ticket?: string;
  };
};

export type EmailDeliveryStatus = 'sent' | 'failed' | 'partial' | 'logged';

export type EmailLogEntry = {
  id: string;
  at: string;
  agentId: string;
  agentEmail: string;
  to: string;
  subject: string;
  kind: 'quote' | 'oaf' | 'ticket_receipt' | 'payment_reminder' | 'test' | 'welcome' | 'ticket_update';
  preview: string;
  /** Set on new log rows; older entries are inferred from preview text. */
  deliveryStatus?: EmailDeliveryStatus;
  errorMessage?: string;
  quoteId?: string;
};

export type CmsDocument = {
  landingHeroTitle: string;
  landingHeroSubtitle: string;
  quoteEmailIntro: string;
  paymentReminderBody: string;
};

export type ClientActivityType =
  | 'quote_sent'
  | 'note'
  | 'client_created'
  | 'status_changed'
  | 'payment_reminder'
  | 'payment_received'
  | 'email_test';

export interface ClientActivity {
  id: string;
  clientId: string;
  at: string;
  type: ClientActivityType;
  title: string;
  detail: string;
}

export interface StoredNotification {
  id: string;
  type: string;
  title: string;
  message: string;
  at: string;
  read: boolean;
}

export type TicketPriority = 'low' | 'medium' | 'high' | 'urgent';
export type TicketStatus = 'open' | 'in_progress' | 'waiting_customer' | 'resolved' | 'closed';

export interface TicketNote {
  id: string;
  message: string;
  author: string;
  createdAt: string;
}

export interface Ticket {
  id: string;
  code: string;
  title: string;
  description: string;
  status: TicketStatus;
  priority: TicketPriority;
  clientName: string;
  clientEmail: string;
  assignee?: string;
  tags: string[];
  notes: TicketNote[];
  createdAt: string;
  updatedAt: string;
}

/** @deprecated Use agents from the API (`useAgents`). Kept empty so old imports fail visibly. */
export const MOCK_AGENTS: Agent[] = [];

export const MOCK_CLIENTS: Client[] = [
  {
    id: 'c1',
    name: 'Alice Johnson',
    email: 'alice@example.com',
    phone: '+1 555 0101',
    requestDate: '2024-05-01',
    status: 'pending',
    origin: 'London',
    destination: 'New York',
    travelDate: '2024-06-15',
    source: 'landing',
  },
  {
    id: 'c2',
    name: 'Bob Wilson',
    email: 'bob@example.com',
    requestDate: '2024-05-02',
    status: 'quoted',
    origin: 'London',
    destination: 'New York',
    source: 'landing',
  },
  {
    id: 'c3',
    name: 'Lead — No email',
    email: '',
    phone: '+44 20 7946 0958',
    requestDate: '2024-05-03',
    status: 'pending',
    origin: 'Paris',
    destination: 'Tokyo',
    travelDate: '2024-07-01',
    source: 'landing',
  },
];

export const MOCK_FLIGHTS: Flight[] = [
  {
    id: 'f1',
    airline: 'Lufthansa',
    flightNumber: 'LH123',
    departure: 'London',
    arrival: 'New York',
    departureTime: '10:00',
    arrivalTime: '13:00',
    price: 450,
    departureHour: 10,
    travelDate: '2024-06-15',
  },
  {
    id: 'f2',
    airline: 'British Airways',
    flightNumber: 'BA456',
    departure: 'London',
    arrival: 'New York',
    departureTime: '14:30',
    arrivalTime: '17:30',
    price: 520,
    departureHour: 14,
    travelDate: '2024-06-15',
  },
  {
    id: 'f3',
    airline: 'Delta',
    flightNumber: 'DL789',
    departure: 'London',
    arrival: 'New York',
    departureTime: '18:15',
    arrivalTime: '21:15',
    price: 480,
    departureHour: 18,
    travelDate: '2024-06-15',
  },
  {
    id: 'f4',
    airline: 'Air France',
    flightNumber: 'AF272',
    departure: 'Paris',
    arrival: 'Tokyo',
    departureTime: '09:40',
    arrivalTime: '06:10',
    price: 890,
    departureHour: 9,
    travelDate: '2024-07-01',
  },
  {
    id: 'f5',
    airline: 'JAL',
    flightNumber: 'JL45',
    departure: 'Paris',
    arrival: 'Tokyo',
    departureTime: '13:20',
    arrivalTime: '10:05',
    price: 940,
    departureHour: 13,
    travelDate: '2024-07-01',
  },
  {
    id: 'f6',
    airline: 'British Airways',
    flightNumber: 'BA112',
    departure: 'London',
    arrival: 'Paris',
    departureTime: '07:00',
    arrivalTime: '09:15',
    price: 120,
    departureHour: 7,
    travelDate: '2024-06-20',
  },
];

export const EMAIL_SECTIONS_META: { id: EmailLogEntry['kind']; label: string; description: string }[] = [
  { id: 'quote', label: 'Quote email', description: 'Branded quote with booking link — client completes passenger details on booking.' },
  { id: 'oaf', label: 'OAF payment email', description: 'Same quote layout with passenger table and payment checkout link (OAF).' },
  { id: 'ticket_receipt', label: 'E-ticket receipt', description: 'Standard ticket email (View ticket + View invoice) after payment.' },
  { id: 'payment_reminder', label: 'Payment reminder', description: 'Optional follow-up from the agent with the payment link.' },
  { id: 'welcome', label: 'Welcome / onboarding', description: 'First touch after a new lead is created manually or from landing.' },
  { id: 'test', label: 'Deliverability test', description: 'Internal test message from the agent mailbox configuration.' },
  { id: 'ticket_update', label: 'Ticket desk email', description: 'Branded HTML sent to the client from the ticket desk.' },
];
